import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
} from '@pantry/ui';
import { useCreateInvitation, type InvitationRole } from '@pantry/api-client';
import { EnvelopeIcon, UserPlusIcon } from '@heroicons/react/24/outline';

interface InviteMemberDialogProps {
  householdId: string;
  householdName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteMemberDialog({
  householdId,
  householdName,
  open,
  onOpenChange,
}: InviteMemberDialogProps) {
  const { t } = useLingui();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InvitationRole>('member');
  const [error, setError] = useState<string | null>(null);

  const createInvitation = useCreateInvitation(householdId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError(t`Email is required`);
      return;
    }

    try {
      await createInvitation.mutateAsync({ email: email.trim(), role });
      setEmail('');
      setRole('member');
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : t`Failed to send invitation`;
      setError(message);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setEmail('');
      setRole('member');
      setError(null);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
              <UserPlusIcon className="h-5 w-5 text-primary" />
            </div>
            <Trans>Invite Member</Trans>
          </DialogTitle>
          <DialogDescription className="pt-2">
            <Trans>
              Send an invitation to join{' '}
              <span className="font-medium text-foreground">{householdName}</span>. They&apos;ll
              receive an email with a link to accept.
            </Trans>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="invite-email">
              <Trans>Email Address</Trans>
            </Label>
            <div className="relative">
              <EnvelopeIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="invite-email"
                type="email"
                placeholder={t`member@example.com`}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 rounded-xl"
                autoFocus
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="invite-role">
              <Trans>Role</Trans>
            </Label>
            <select
              id="invite-role"
              value={role}
              onChange={(e) => setRole(e.target.value as InvitationRole)}
              className="flex h-10 w-full rounded-xl border border-border/50 bg-muted/30 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:border-primary/50 transition-all cursor-pointer hover:border-border"
            >
              <option value="member">{t`Member - Can view and edit items`}</option>
              <option value="admin">{t`Admin - Can also manage members and settings`}</option>
            </select>
            <p className="text-xs text-muted-foreground">
              <Trans>You can change their role later from the members list.</Trans>
            </p>
          </div>

          {error && (
            <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive animate-in fade-in slide-in-from-top-2">
              {error}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={createInvitation.isPending}
              className="rounded-xl"
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button type="submit" disabled={createInvitation.isPending} className="rounded-xl">
              {createInvitation.isPending ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  <Trans>Sending...</Trans>
                </div>
              ) : (
                <Trans>Send Invitation</Trans>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
